import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Factory,
  Zap,
  Building2,
  AlertCircle,
  Clock,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { api } from "@/lib/api";
import SEO, { commoditySchema } from "@/components/SEO";

const STATUS_TAG = {
  advancing: "bb-tag-positive",
  permitting: "bb-tag-neutral",
  delayed: "bb-tag-negative",
  operational: "bb-tag-positive",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatPrice = (p) =>
  p >= 1000
    ? p.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : p.toFixed(2);

export default function CommodityDetailPage() {
  const { slug } = useParams();
  const [commodity, setCommodity] = useState(null);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [articles, setArticles] = useState([]);
  const [ticker, setTicker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    setCommodity(null);
    setProjects([]);
    setCompanies([]);
    setArticles([]);
    setTicker(null);

    api
      .get(`/commodities/${slug}`)
      .then(async (r) => {
        if (!mounted) return;
        const c = r.data;
        setCommodity(c);

        const [projRes, coRes, artRes, tickerRes] = await Promise.all([
          api.get("/projects"),
          api.get("/companies"),
          api.get("/articles"),
          api.get("/market-ticker"),
        ]);
        if (!mounted) return;

        setProjects(
          (Array.isArray(projRes.data) ? projRes.data : []).filter((p) =>
            (p.commodity || []).includes(c.name),
          ),
        );
        setCompanies(
          (Array.isArray(coRes.data) ? coRes.data : []).filter((co) =>
            (co.commodities || []).includes(c.name),
          ),
        );
        setArticles(
          (Array.isArray(artRes.data) ? artRes.data : [])
            .filter((a) => a.commodity === c.id)
            .slice(0, 6),
        );
        const t = (Array.isArray(tickerRes.data) ? tickerRes.data : []).find(
          (x) => x.commoditySlug === c.slug,
        );
        setTicker(t || null);
      })
      .catch(() => mounted && setError(true))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div
        className="bb-container py-20 text-center bb-overline text-bb-slate"
        data-testid="commodity-loading"
      >
        Loading commodity…
      </div>
    );
  }

  if (error || !commodity) {
    return (
      <section className="bb-container py-20" data-testid="commodity-not-found">
        <div className="border border-border bg-white p-12 text-center max-w-2xl mx-auto">
          <AlertCircle className="text-bb-copper mx-auto" size={28} />
          <div className="bb-overline text-bb-copper mt-4">
            404 · Commodity
          </div>
          <h1 className="font-archivo text-3xl text-bb-navy mt-3 font-bold">
            Commodity not found.
          </h1>
          <Link
            to="/commodities"
            className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
            data-testid="commodity-back-to-list"
          >
            <ArrowLeft size={14} /> Back to commodities
          </Link>
        </div>
      </section>
    );
  }

  const positive = ticker && ticker.change_pct >= 0;

  return (
    <article data-testid={`commodity-detail-${commodity.slug}`}>
      <SEO
        title={`${commodity.name} — Canadian mining coverage`}
        description={commodity.canadianContext || commodity.description}
        type="article"
        jsonLd={commoditySchema(commodity)}
      />
      {/* Hero */}
      <header className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <Link
            to="/commodities"
            className="bb-link inline-flex items-center gap-2 text-xs font-archivo uppercase tracking-widest text-bb-navy mb-8"
            data-testid="commodity-back-link"
          >
            <ArrowLeft size={14} /> Back to Commodities
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 flex flex-col">
              <div className="flex items-center gap-3">
                <span className="bb-overline text-bb-copper">
                  {commodity.category}
                </span>
                {commodity.criticalMineral && (
                  <span
                    className="inline-flex items-center gap-1 bb-tag-negative text-[10px] font-mono px-2 py-1"
                    data-testid="commodity-critical-badge"
                  >
                    <Zap size={10} strokeWidth={2.5} />
                    Critical Mineral
                  </span>
                )}
              </div>
              <h1
                className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
                data-testid="commodity-title"
              >
                {commodity.name}
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate leading-relaxed max-w-2xl">
                {commodity.description}
              </p>
            </div>

            {/* Market ticker snapshot */}
            <div className="lg:col-span-5">
              <div
                className="bg-bb-navy text-white p-6 border border-bb-navy"
                data-testid="commodity-ticker-snapshot"
              >
                <div className="bb-overline text-white/60">Live · Market</div>
                {ticker ? (
                  <>
                    <div className="mt-3 flex items-baseline gap-3">
                      <span className="font-archivo font-bold text-4xl lg:text-5xl">
                        {formatPrice(ticker.price)}
                      </span>
                      <span className="font-mono text-sm text-white/60">
                        {ticker.unit}
                      </span>
                    </div>
                    <div
                      className={`mt-3 inline-flex items-center gap-1.5 font-mono text-sm ${positive ? "text-emerald-300" : "text-orange-300"}`}
                    >
                      {positive ? (
                        <ArrowUp size={14} strokeWidth={2.5} />
                      ) : (
                        <ArrowDown size={14} strokeWidth={2.5} />
                      )}
                      {positive ? "+" : ""}
                      {ticker.change_pct.toFixed(2)}% today
                    </div>
                    <div className="mt-5 pt-4 border-t border-white/15 font-mono text-[11px] text-white/50">
                      {ticker.symbol} ·{" "}
                      {ticker.source && ticker.source !== "reference"
                        ? `Live via ${ticker.source.replace("_", " ")}${ticker.as_of ? ` · as of ${new Date(ticker.as_of).toLocaleDateString("en-CA")}` : ""}`
                        : "Reference / editorial estimate"}
                    </div>
                  </>
                ) : (
                  <div className="mt-5 text-sm text-white/70 font-mono">
                    Not currently ticked in the BedrockBulletin market strip.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Uses + Canadian Context */}
      <section className="py-12 lg:py-16">
        <div className="bb-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5" data-testid="commodity-uses-panel">
              <div className="bb-overline text-bb-copper">Applications</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Key uses
              </h2>
              <ul className="space-y-3">
                {(commodity.uses || []).map((u, i) => (
                  <li
                    key={u}
                    className="bg-white border border-border p-4 flex items-start gap-4"
                    data-testid={`commodity-use-${i}`}
                  >
                    <span className="font-mono text-xs text-bb-copper font-bold min-w-[24px] pt-0.5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm text-bb-navy leading-relaxed">
                      {u}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="lg:col-span-7"
              data-testid="commodity-canadian-context-panel"
            >
              <div className="bb-overline text-bb-copper">Canadian context</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Where {commodity.name} sits in Canada
              </h2>
              <div className="bg-white border border-border p-6 lg:p-8">
                <p className="text-base text-bb-slate leading-relaxed">
                  {commodity.canadianContext}
                </p>
                <div className="mt-6 pt-6 border-t border-border grid grid-cols-3 gap-4">
                  <div>
                    <div className="bb-overline text-bb-slate">Projects</div>
                    <div className="font-mono text-2xl text-bb-navy mt-1 font-semibold">
                      {projects.length}
                    </div>
                  </div>
                  <div>
                    <div className="bb-overline text-bb-slate">Operators</div>
                    <div className="font-mono text-2xl text-bb-navy mt-1 font-semibold">
                      {companies.length}
                    </div>
                  </div>
                  <div>
                    <div className="bb-overline text-bb-slate">Coverage</div>
                    <div className="font-mono text-2xl text-bb-navy mt-1 font-semibold">
                      {articles.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section
        className="py-12 lg:py-16 border-t border-border bg-white"
        data-testid="commodity-projects-section"
      >
        <div className="bb-container">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="bb-overline text-bb-copper">Project pipeline</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 font-bold tracking-tight">
                Canadian {commodity.name} projects
              </h2>
            </div>
            <Link
              to={`/projects?commodity=${encodeURIComponent(commodity.name)}`}
              className="bb-link text-xs font-archivo uppercase tracking-widest text-bb-navy inline-flex items-center gap-1"
              data-testid="commodity-projects-link"
            >
              View in database
              <ArrowUpRight size={14} strokeWidth={2.4} />
            </Link>
          </div>

          {projects.length === 0 ? (
            <div className="border border-dashed border-border bg-bb-offwhite p-8 text-sm text-bb-slate font-mono">
              No Canadian {commodity.name} projects currently in the
              BedrockBulletin database.
            </div>
          ) : (
            <div className="bg-white border border-border overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border bg-bb-offwhite">
                    <th className="p-4 bb-overline text-bb-slate">Project</th>
                    <th className="p-4 bb-overline text-bb-slate">Province</th>
                    <th className="p-4 bb-overline text-bb-slate">Stage</th>
                    <th className="p-4 bb-overline text-bb-slate">Status</th>
                    <th className="p-4 bb-overline text-bb-slate">Capex</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-border last:border-b-0 hover:bg-bb-offwhite transition-colors"
                      data-testid={`commodity-project-${p.slug}`}
                    >
                      <td className="p-4 align-top">
                        <div className="font-archivo font-semibold text-bb-navy">
                          <Link to={`/projects/${p.slug}`} className="bb-link">
                            {p.name}
                          </Link>
                        </div>
                      </td>
                      <td className="p-4 align-top text-xs font-mono text-bb-slate">
                        {p.province}
                      </td>
                      <td className="p-4 align-top bb-overline text-bb-navy">
                        {p.stage}
                      </td>
                      <td className="p-4 align-top">
                        <span
                          className={`text-[11px] font-mono px-2 py-1 ${STATUS_TAG[p.status] || "bb-tag-neutral"}`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="p-4 align-top font-mono text-sm text-bb-navy">
                        {p.capexEstimate
                          ? `$${p.capexEstimate.toLocaleString()}M`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Operators + Coverage */}
      <section className="py-12 lg:py-16 border-t border-border">
        <div className="bb-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div
              className="lg:col-span-5"
              data-testid="commodity-operators-section"
            >
              <div className="bb-overline text-bb-copper">Operators</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Canadian {commodity.name} names
              </h2>
              {companies.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-6 text-sm text-bb-slate font-mono">
                  No operators currently tagged to {commodity.name}.
                </div>
              ) : (
                <div className="space-y-3">
                  {companies.map((co) => (
                    <Link
                      key={co.id}
                      to={`/companies/${co.slug}`}
                      className="bb-card flex items-center gap-4 p-4 group"
                      data-testid={`commodity-company-${co.slug}`}
                    >
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                        <Building2 size={16} strokeWidth={1.8} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-archivo font-semibold text-bb-navy truncate">
                          {co.name}
                        </div>
                        <div className="text-[11px] font-mono text-bb-slate">
                          {co.ticker} · {co.exchange}
                        </div>
                      </div>
                      <ArrowUpRight
                        size={14}
                        className="text-bb-navy group-hover:text-bb-copper transition-colors"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div
              className="lg:col-span-7"
              data-testid="commodity-coverage-section"
            >
              <div className="bb-overline text-bb-copper">Coverage</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Recent dispatches
              </h2>
              {articles.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-6 text-sm text-bb-slate font-mono">
                  No dispatches on the desk mentioning {commodity.name} yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {articles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/news/${a.slug}`}
                      className="bb-card p-5 flex flex-col gap-2 group"
                      data-testid={`commodity-article-${a.slug}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="bb-overline text-bb-copper">
                          {a.category}
                        </span>
                        <span className="text-[11px] font-mono text-bb-slate">
                          {formatDate(a.publishedDate)}
                        </span>
                      </div>
                      <h3 className="font-archivo text-lg text-bb-navy font-semibold tracking-tight leading-snug">
                        <span className="bb-link">{a.title}</span>
                      </h3>
                      <p className="text-sm text-bb-slate leading-relaxed">
                        {a.excerpt}
                      </p>
                      <div className="mt-1 pt-3 border-t border-border flex items-center justify-between">
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-slate">
                          <Clock size={11} /> By {a.author}
                        </span>
                        <ArrowUpRight
                          size={14}
                          className="text-bb-navy group-hover:text-bb-copper transition-colors"
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
